import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { MyManagedStoresReference } from "../hooks";
import { MANAGED_STORES_QUERY_KEY } from "../hooks";
import { patchStoreProvisioning } from "../api";
import type { ManagedStoreRow } from "../types";
import type { CatalogSliceGroup } from "../managedStoresReference";
import "../../../App.css";

const STORE_PREVIEW = 5;
const ROLE_PREVIEW = 10;

function IconExternalSmall() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path
        d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconCopySmall() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function IconPersonSmall() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.75" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function CatalogSliceCard({
  g,
  sliceIndex,
  onCopyStoreIds,
  onCopied
}: {
  g: CatalogSliceGroup;
  sliceIndex: number;
  onCopyStoreIds: () => Promise<void>;
  onCopied: (msg: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [fullCatalog, setFullCatalog] = useState(false);
  const [allRoles, setAllRoles] = useState(false);

  const q = search.trim().toLowerCase();

  const filteredStoreIds = useMemo(() => {
    if (!q) {
      return g.storeIds;
    }
    return g.storeIds.filter((id) => id.toLowerCase().includes(q));
  }, [g.storeIds, q]);

  const filteredRoles = useMemo(() => {
    if (!q) {
      return g.roles;
    }
    return g.roles.filter((r) => r.toLowerCase().includes(q));
  }, [g.roles, q]);

  useEffect(() => {
    if (!fullCatalog) {
      setAllRoles(false);
    }
  }, [fullCatalog]);

  const totalStores = g.storeIds.length;
  const totalRoles = g.roles.length;

  const storesExpandedView = fullCatalog;
  const visibleStoreIds = storesExpandedView ? filteredStoreIds : filteredStoreIds.slice(0, STORE_PREVIEW);
  const hiddenStoreCount = storesExpandedView ? 0 : Math.max(0, filteredStoreIds.length - STORE_PREVIEW);

  const rolesExpandedView = fullCatalog || allRoles;
  const visibleRoles = rolesExpandedView ? filteredRoles : filteredRoles.slice(0, ROLE_PREVIEW);
  const hiddenRoleCount = rolesExpandedView ? 0 : Math.max(0, filteredRoles.length - ROLE_PREVIEW);

  const title = [g.group, g.concept].filter(Boolean).join(" · ") || "Catalog slice";

  const storeFoot =
    filteredStoreIds.length === 0
      ? q
        ? "No store IDs match your search."
        : "No store IDs in this slice."
      : storesExpandedView
        ? `Showing all ${filteredStoreIds.length} store ID${filteredStoreIds.length === 1 ? "" : "s"}${q ? ` (of ${totalStores} total)` : ""}.`
        : `Showing first ${Math.min(STORE_PREVIEW, filteredStoreIds.length)} of ${filteredStoreIds.length} store ID${filteredStoreIds.length === 1 ? "" : "s"}${q && filteredStoreIds.length !== totalStores ? ` (${totalStores} total in slice)` : ""}.`;

  const roleFoot =
    filteredRoles.length === 0
      ? q
        ? "No roles match your search."
        : "No roles listed for this slice."
      : rolesExpandedView
        ? `Showing all ${filteredRoles.length} role${filteredRoles.length === 1 ? "" : "s"}${q ? ` (of ${totalRoles} total)` : ""}.`
        : `Showing first ${Math.min(ROLE_PREVIEW, filteredRoles.length)} of ${filteredRoles.length} role${filteredRoles.length === 1 ? "" : "s"}${q && filteredRoles.length !== totalRoles ? ` (${totalRoles} total in slice)` : ""}.`;

  return (
    <article className="catalog-slice-card">
      <div className="catalog-slice-card-body">
        <div className="catalog-slice-card-row1">
          <div className="catalog-slice-card-title">
            <span style={{ fontSize: "1.1rem" }} aria-hidden>
              🏪
            </span>
            <span>{title}</span>
          </div>
          <div className="catalog-slice-card-actions">
            <button
              type="button"
              className="catalog-slice-linkish"
              aria-expanded={fullCatalog}
              onClick={() => setFullCatalog((v) => !v)}
            >
              {fullCatalog ? (
                "Show summary"
              ) : (
                <>
                  View full catalog details
                  <IconExternalSmall />
                </>
              )}
            </button>
            <button
              type="button"
              className="catalog-slice-copy-btn"
              onClick={() =>
                void onCopyStoreIds().then(() => onCopied(`Copied ${g.storeIds.length} store IDs for this slice`))
              }
            >
              <IconCopySmall />
              Copy store IDs
            </button>
          </div>
        </div>

        <div className="catalog-slice-badges-row">
          <span className="catalog-slice-count-pill catalog-slice-count-pill--stores">
            {totalStores} store ID{totalStores === 1 ? "" : "s"}
          </span>
          <span className="catalog-slice-count-pill catalog-slice-count-pill--roles">
            {totalRoles} role{totalRoles === 1 ? "" : "s"}
          </span>
        </div>

        <label className="visually-hidden" htmlFor={`catalog-slice-search-${sliceIndex}`}>
          Search store IDs or roles for {title}
        </label>
        <input
          id={`catalog-slice-search-${sliceIndex}`}
          type="search"
          className="catalog-slice-search"
          placeholder="Search store IDs or roles"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoComplete="off"
        />

        <div className="catalog-slice-two-col">
          <div>
            <h3 className="catalog-slice-col-title">
              Store IDs ({filteredStoreIds.length}
              {q && filteredStoreIds.length !== totalStores ? ` of ${totalStores}` : ""})
            </h3>
            {filteredStoreIds.length === 0 ? (
              <p className="catalog-slice-foot-muted" style={{ marginTop: 0 }}>
                {storeFoot}
              </p>
            ) : (
              <>
                <div className="catalog-slice-pill-row">
                  {visibleStoreIds.map((id) => (
                    <span key={id} className="catalog-slice-store-pill" title={id}>
                      {id}
                    </span>
                  ))}
                  {hiddenStoreCount > 0 ? (
                    <span className="catalog-slice-more-pill">+{hiddenStoreCount} more</span>
                  ) : null}
                </div>
                <p className="catalog-slice-foot-muted">{storeFoot}</p>
              </>
            )}
          </div>

          <div>
            <h3 className="catalog-slice-col-title">
              Available roles ({filteredRoles.length}
              {q && filteredRoles.length !== totalRoles ? ` of ${totalRoles}` : ""})
            </h3>
            {filteredRoles.length === 0 ? (
              <p className="catalog-slice-foot-muted" style={{ marginTop: 0 }}>
                {roleFoot}
              </p>
            ) : (
              <>
                <ul
                  className={`catalog-slice-roles-list ${rolesExpandedView ? "catalog-slice-roles-list--expanded" : ""}`}
                >
                  {visibleRoles.map((role) => (
                    <li key={role} className="catalog-slice-role-row">
                      <span style={{ flexShrink: 0, marginTop: "0.06rem" }}>
                        <IconPersonSmall />
                      </span>
                      <span>{role}</span>
                    </li>
                  ))}
                </ul>
                <p className="catalog-slice-foot-muted">{roleFoot}</p>
                {!fullCatalog && hiddenRoleCount > 0 && !allRoles ? (
                  <button type="button" className="catalog-slice-linkish" onClick={() => setAllRoles(true)}>
                    View all roles →
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: "auto",
          padding: "0.65rem 1.15rem",
          background: "linear-gradient(180deg, #e0f2fe 0%, #dbeafe 100%)",
          borderTop: "1px solid #bae6fd",
          fontSize: "0.78rem",
          color: "#0c4a6e",
          lineHeight: 1.45,
          display: "flex",
          gap: "0.4rem",
          alignItems: "flex-start"
        }}
      >
        <span aria-hidden style={{ flexShrink: 0 }}>
          ℹ️
        </span>
        <span>
          Use the same role labels shown here in your CSV.
          {g.locationSample ? (
            <>
              {" "}
              Sample location: <strong style={{ fontWeight: 700 }}>{g.locationSample}</strong>
            </>
          ) : null}
        </span>
      </div>
    </article>
  );
}

function StoreProvisioningTogglesPanel({ rows }: { rows: ManagedStoreRow[] }) {
  const queryClient = useQueryClient();
  const [busyStoreId, setBusyStoreId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ownerRows = useMemo(
    () => rows.filter((r) => r.store_id && r.store_id.trim().length > 0),
    [rows]
  );

  if (ownerRows.length === 0) {
    return null;
  }

  const flipMethod = async (row: ManagedStoreRow) => {
    setError(null);
    setBusyStoreId(row.store_id);
    try {
      const nextMethod =
        (row.provisioning_method || "csv") === "self_registration" ? "csv" : "self_registration";
      await patchStoreProvisioning(row.store_id, { provisioning_method: nextMethod });
      await queryClient.invalidateQueries({ queryKey: MANAGED_STORES_QUERY_KEY });
    } catch (e) {
      const maybeAxios = e as { response?: { data?: { detail?: unknown } }; message?: string };
      setError(
        (typeof maybeAxios?.response?.data?.detail === "string"
          ? (maybeAxios.response.data.detail as string)
          : maybeAxios?.message) || "Could not update provisioning method."
      );
    } finally {
      setBusyStoreId(null);
    }
  };

  return (
    <section
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: "0.85rem",
        padding: "1rem 1.15rem",
        background: "#ffffff"
      }}
    >
      <header style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#0f172a" }}>
            Provisioning method per location
          </h3>
          <p style={{ margin: "0.25rem 0 0", fontSize: "0.82rem", color: "#64748b" }}>
            Switch a store between CSV upload and self-registration without re-uploading the CSV.
          </p>
        </div>
      </header>

      {error ? (
        <div style={{ marginTop: "0.75rem", color: "#b91c1c", fontSize: "0.85rem" }} role="alert">
          {error}
        </div>
      ) : null}

      <ul style={{ listStyle: "none", padding: 0, margin: "0.75rem 0 0", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {ownerRows.map((row) => {
          const method = row.provisioning_method || "csv";
          const isSelfReg = method === "self_registration";
          const busy = busyStoreId === row.store_id;
          return (
            <li
              key={row.store_id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.75rem",
                padding: "0.55rem 0.75rem",
                background: "#f8fafc",
                borderRadius: "0.6rem",
                border: "1px solid #e2e8f0"
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: "0.92rem", color: "#0f172a" }}>
                  {row.business_name}
                </div>
                <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "0.78rem", color: "#64748b" }}>
                  {row.store_id}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span
                  style={{
                    fontSize: "0.78rem",
                    color: isSelfReg ? "#047857" : "#64748b",
                    fontWeight: 600
                  }}
                  aria-live="polite"
                >
                  {isSelfReg ? "Self-registration" : "CSV upload"}
                </span>
                <button
                  type="button"
                  onClick={() => void flipMethod(row)}
                  disabled={busy}
                  style={{
                    padding: "0.4rem 0.75rem",
                    borderRadius: "999px",
                    border: "1px solid #2563eb",
                    backgroundColor: busy ? "#e0e7ff" : "#dbeafe",
                    color: "#1d4ed8",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    cursor: busy ? "wait" : "pointer"
                  }}
                >
                  {busy ? "Saving…" : isSelfReg ? "Switch to CSV" : "Switch to self-reg"}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}


interface ManagedStoresReferenceCardsProps {
  reference: MyManagedStoresReference;
  /** Same handler as dashboard “Download template” (blank CSV). */
  onDownloadTemplate?: () => void;
}

/**
 * Grouped by Margaritaville catalog slice (shared roles per cluster).
 * Compact cards with search, expandable details, and copy for store IDs.
 */
const ManagedStoresReferenceCards = ({ reference, onDownloadTemplate }: ManagedStoresReferenceCardsProps) => {
  const { storesQuery, groups, rows, rolesLoading, rolesError, hasStores, copyGroupStoreIds } = reference;

  const [copyHint, setCopyHint] = useState<string | null>(null);

  const flashHint = (msg: string) => {
    setCopyHint(msg);
    window.setTimeout(() => setCopyHint(null), 2200);
  };

  return (
    <section
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: "1rem",
        padding: "1.25rem 1.5rem",
        backgroundColor: "#f8fafc",
        display: "flex",
        flexDirection: "column",
        gap: "1rem"
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ flex: "1 1 280px" }}>
          <h2 style={{ margin: 0, fontSize: "1.35rem", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
            Locations & role catalog
          </h2>
          <p style={{ margin: "0.4rem 0 0", fontSize: "0.9rem", color: "#64748b", maxWidth: "720px", lineHeight: 1.55 }}>
            Reference for filling CSVs: each card is one catalog slice (shared roles). Use the same{" "}
            <strong style={{ fontWeight: 600, color: "#334155" }}>role</strong> strings in your file’s{" "}
            <span style={{ fontFamily: "ui-monospace, monospace" }}>role</span> column and these{" "}
            <span style={{ fontFamily: "ui-monospace, monospace" }}>store_id</span> values. Start from{" "}
            <strong style={{ fontWeight: 600, color: "#334155" }}>Download template</strong> and{" "}
            <strong style={{ fontWeight: 600, color: "#334155" }}>New upload</strong> in the{" "}
            <strong style={{ fontWeight: 600, color: "#334155" }}>Provisioning jobs</strong> section above when
            ready (same row as Refresh).
          </p>
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            alignItems: "center",
            justifyContent: "flex-end"
          }}
        >
          {onDownloadTemplate ? (
            <button type="button" className="dashboard-btn-outline" onClick={() => onDownloadTemplate()}>
              Download template
            </button>
          ) : null}
        </div>
      </div>

      {copyHint ? (
        <div style={{ fontSize: "0.82rem", color: "#047857", fontWeight: 600 }} role="status">
          {copyHint}
        </div>
      ) : null}

      {storesQuery.isLoading ? (
        <div style={{ color: "#94a3b8", fontSize: "0.9rem" }}>Loading your locations…</div>
      ) : storesQuery.isError ? (
        <div style={{ color: "#b91c1c", fontSize: "0.9rem" }}>Could not load managed stores. Refresh and try again.</div>
      ) : !hasStores ? (
        <div style={{ color: "#64748b", fontSize: "0.9rem", lineHeight: 1.5 }}>
          No store assignments found for this account yet. If you were just onboarded, finish linking your slice first.
        </div>
      ) : (
        <>
          <StoreProvisioningTogglesPanel rows={rows} />
          {rolesError ? (
            <div style={{ color: "#b45309", fontSize: "0.85rem" }}>
              Some role lists failed to load — cards may be incomplete. Try refresh.
            </div>
          ) : null}
          {rolesLoading ? (
            <div style={{ color: "#94a3b8", fontSize: "0.85rem" }}>Loading catalog roles for grouping…</div>
          ) : null}
          <div className="catalog-slices-grid">
            {groups.map((g, sliceIndex) => (
              <CatalogSliceCard
                key={`${g.group}|${g.concept}|${g.storeIds.join(",")}`}
                sliceIndex={sliceIndex}
                g={g}
                onCopyStoreIds={() => copyGroupStoreIds(g, "comma")}
                onCopied={flashHint}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default ManagedStoresReferenceCards;
